import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Skeleton } from '@mui/material';
import { useGetTransactionHistoryQuery } from 'store/services/transaction/transactionApi';
import { PointTransaction } from 'types/interfaces/transaction';
import Points from 'components/Points';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import './TransactionHistoryPage.css';

const TransactionHistoryPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data, isLoading, error, refetch } = useGetTransactionHistoryQuery({
    page,
    pageSize,
  });

  // Format date as MM/DD/YY HH:MM (UTC)
  const formatTransactionDate = (isoDateString: string): string => {
    const date = new Date(isoDateString);

    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = String(date.getUTCFullYear()).slice(-2);
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');

    return `${month}/${day}/${year} ${hours}:${minutes}`;
  };

  // Get error message based on error status
  const getErrorMessage = (error: any): string => {
    const status = error?.status || error?.originalStatus;

    if (status === 401) return "Please log in to view your transactions";
    if (status === 403) return "You do not have permission to view this resource";
    if (status === 404) return "Transaction history is temporarily unavailable";
    if (status === 500) return "Server error. Our team has been notified. Please try again in a few minutes.";
    if (status === 'FETCH_ERROR') return "Network error. Please check your internet connection.";
    if (status === 'TIMEOUT_ERROR') return "Request timed out. Please try again.";

    return "Unable to load transaction history. Please try again.";
  };

  // Determine if retry button should be shown
  const shouldShowRetry = (error: any): boolean => {
    const status = error?.status || error?.originalStatus;
    return status !== 401 && status !== 403;
  };

  // Handle pagination
  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (data && page < data.pagination.totalPages) {
      setPage(page + 1);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="transaction-history-page">
        <div className="transaction-history-header">
          <h1>Transaction History</h1>
          <p className="transaction-history-subtitle">
            View all point transactions and balance changes
          </p>
        </div>

        <div className="transaction-history-content">
          <div className="transaction-table">
            <div className="transaction-table-header">
              <div className="transaction-col-date">Date</div>
              <div className="transaction-col-type">Type</div>
              <div className="transaction-col-amount">Amount</div>
              <div className="transaction-col-description">Description</div>
            </div>
            <div className="transaction-history-loading">
              {[...Array(10)].map((_, index) => (
                <div key={index} className="transaction-row-skeleton">
                  <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  <Skeleton variant="text" width={140} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  <Skeleton variant="text" width="100%" height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="transaction-history-page">
        <div className="transaction-history-header">
          <h1>Transaction History</h1>
        </div>

        <div className="transaction-history-content">
          <div className="transaction-history-error-state">
            <div className="error-icon">⚠️</div>
            <h2 className="error-title">Unable to Load Transactions</h2>
            <p className="error-message">{getErrorMessage(error)}</p>
            {shouldShowRetry(error) && (
              <MagicButton
                label="Try Again"
                color="purple"
                onPress={() => refetch()}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (data && data.transactions.length === 0) {
    return (
      <div className="transaction-history-page">
        <div className="transaction-history-header">
          <h1>Transaction History</h1>
          <p className="transaction-history-subtitle">
            View all point transactions and balance changes
          </p>
        </div>

        <div className="transaction-history-content">
          <div className="transaction-history-empty-state">
            <div className="empty-state-icon">
              <img
                src={`${AssetUrls.base}/assets/icons/receipt.svg`}
                alt="No transactions"
                style={{ width: 64, height: 64, opacity: 0.3 }}
                onError={(e) => {
                  // Fallback if receipt icon doesn't exist
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
            <h2 className="empty-state-title">No Transactions Yet</h2>
            <p className="empty-state-description">
              Your transaction history will appear here once you start trading,
              receiving bonuses, or when system fees are applied.
            </p>
            <MagicButton
              label="Start Trading"
              color="purple"
              onPress={() => navigate('/search')}
            />
          </div>
        </div>
      </div>
    );
  }

  // Success state with data
  return (
    <div className="transaction-history-page">
      <div className="transaction-history-header">
        <h1>Transaction History</h1>
        <p className="transaction-history-subtitle">
          View all point transactions and balance changes
        </p>
      </div>

      <div className="transaction-history-content">
        <div className="transaction-table">
          {/* Table Header */}
          <div className="transaction-table-header">
            <div className="transaction-col-date">Date</div>
            <div className="transaction-col-type">Type</div>
            <div className="transaction-col-amount">Amount</div>
            <div className="transaction-col-description">Description</div>
          </div>

          {/* Table Body */}
          <div className="transaction-table-body">
            {data?.transactions.map((transaction: PointTransaction) => (
              <div key={transaction.id} className="transaction-row">
                <div className="transaction-col-date">
                  {formatTransactionDate(transaction.createdAt)}
                </div>
                <div className="transaction-col-type">
                  {transaction.typeDisplay}
                </div>
                <div className="transaction-col-amount">
                  <div className={`transaction-amount ${transaction.amount >= 0 ? 'positive' : 'negative'}`}>
                    <span className="amount-sign">
                      {transaction.amount >= 0 ? '+' : '-'}
                    </span>
                    <Points points={Math.abs(transaction.amount)} type="small" />
                  </div>
                </div>
                <div className="transaction-col-description">
                  {transaction.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Controls */}
        {data && data.pagination.totalPages > 1 && (
          <div className="pagination-controls">
            <MagicButton
              label="Previous"
              color="grey"
              onPress={handlePreviousPage}
              isDisabled={page === 1}
            />

            <span className="pagination-info">
              Page {data.pagination.currentPage} of {data.pagination.totalPages}
              {' '}({data.pagination.totalTransactions} total transactions)
            </span>

            <MagicButton
              label="Next"
              color="grey"
              onPress={handleNextPage}
              isDisabled={page >= data.pagination.totalPages}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistoryPage;
